import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2, MapPin, Clock, CloudCheck, CloudAlert, Camera } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMessage, errFields, focusFirstError } from "@/lib/api";
import { fromDoc, validateTake5 } from "@/lib/take5";
import { durationLabel, addMinutes, fmtTime } from "@/lib/time";
import { Section, FieldError } from "@/components/common";
import { PhotoInput, AuthImage } from "@/components/Photo";
import { selectCls } from "@/pages/Take5New";
import { Take5Header } from "@/components/take5/Take5Header";
import { Questions } from "@/components/take5/Questions";
import { Hazards } from "@/components/take5/Hazards";
import { StopButton } from "@/components/take5/Dialogs";
import { CollectiveParticipants } from "@/components/take5/Participants";

const Context = ({ d, refs, errors, set }) => (
  <Section title="Contexte de la tâche" icon={MapPin} testId="take5-context-section">
    <div className="space-y-4">
      <div data-field="task">
        <label htmlFor="task" className="text-sm font-semibold">Description de la tâche</label>
        <Textarea id="task" data-testid="take5-task-input" rows={2} value={d.task} onChange={(e) => set({ task: e.target.value }, "task")} placeholder="Ex. Installer un ordinateur" className="mt-1.5 text-base" />
        <FieldError error={errors.task} testId="take5-task-error" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-4">
          <div data-field="site_id">
            <label htmlFor="site" className="text-sm font-semibold">Site</label>
            <select id="site" data-testid="take5-site-select" className={`${selectCls} mt-1.5`} value={d.site_id} onChange={(e) => set({ site_id: e.target.value }, "site_id")}>
              {refs.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <FieldError error={errors.site_id} />
          </div>
          <div data-field="duration_minutes">
            <label htmlFor="duration" className="flex items-center gap-1 text-sm font-semibold"><Clock className="h-4 w-4 text-amber-400" /> Durée prévue</label>
            <select id="duration" data-testid="take5-duration-select" className={`${selectCls} mt-1.5`} value={d.duration_minutes || ""}
              onChange={(e) => set({ duration_minutes: e.target.value ? Number(e.target.value) : null }, "duration_minutes")}>
              <option value="">— Choisir la durée —</option>
              {refs.durations.map((m) => <option key={m} value={m}>{durationLabel(m)}</option>)}
            </select>
            <FieldError error={errors.duration_minutes} testId="take5-duration-error" />
          </div>
        </div>
        <div data-field="location">
          <label htmlFor="location" className="text-sm font-semibold">Lieu précis</label>
          <Input id="location" data-testid="take5-location-input" value={d.location} onChange={(e) => set({ location: e.target.value }, "location")} placeholder="Ex. Bureau 204, 2e étage" className="mt-1.5 h-12 text-base" />
          <FieldError error={errors.location} testId="take5-location-error" />
        </div>
      </div>
      <div data-field="participants">
        <p className="text-sm font-semibold">Mode : <span data-testid="take5-mode-display" className="text-amber-500">{d.mode === "collectif" ? "Collectif" : "Individuel"}</span></p>
        {d.mode === "collectif" && (
          <div className="mt-3">
            <CollectiveParticipants d={d} set={set} members={refs.members} />
          </div>
        )}
        <FieldError error={errors.participants} testId="take5-participants-error" />
      </div>
    </div>
  </Section>
);

const Photos = ({ doc, d, set }) => (
  <Section title="Photos (facultatif)" icon={Camera} testId="take5-photos-section">
    <div className="flex flex-wrap gap-3">
      {d.photo_ids.map((pid) => <AuthImage key={pid} fileId={pid} alt="Photo du Take 5" className="h-20 w-20 rounded-lg" />)}
    </div>
    <div className="mt-3">
      <PhotoInput linkedType="take5" linkedId={doc.id} value={null} label="Ajouter une photo de la zone de travail" testId="take5-photo-upload"
        onChange={(fid) => set({ photo_ids: [...d.photo_ids, fid] })} />
    </div>
  </Section>
);

export const Take5Form = ({ doc, refs, onDecided }) => {
  const [d, setD] = useState(() => fromDoc(doc));
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState({ status: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);
  const first = useRef(true);
  const decided = useRef(false);
  const siteObj = refs.sites.find((s) => s.id === d.site_id) || doc.site;
  const viewDoc = { ...doc, site: siteObj, task: d.task };

  const set = (patch, errKey) => {
    setD((prev) => ({ ...prev, ...patch }));
    if (errKey) setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== errKey && !(errKey === "hazards" && k.startsWith("hazards")))));
  };

  const save = useCallback(async (data) => {
    if (decided.current) return;
    setSaveState({ status: "saving" });
    try {
      const r = await api.put(`/take5s/${doc.id}`, data);
      setSaveState({ status: "saved", at: r.data.updated_at });
    } catch (e) {
      if (!decided.current) setSaveState({ status: "error", msg: errMessage(e) });
    }
  }, [doc.id]);

  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(d), 1200);
    return () => clearTimeout(timer.current);
  }, [d, save]);

  const fail = (errs, msg) => {
    setErrors(errs);
    focusFirstError(errs);
    toast.error(msg || `Tâche non autorisée : ${Object.keys(errs).length} point(s) à corriger.`);
  };

  const authorize = async () => {
    if (submitting) return;
    clearTimeout(timer.current);
    const errs = validateTake5(doc, d);
    if (Object.keys(errs).length) { save(d); return fail(errs); }
    setSubmitting(true);
    try {
      const r = await api.post(`/take5s/${doc.id}/authorize`, d);
      decided.current = true;
      toast.success("Tâche autorisée : les données et la décision sont enregistrées.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDecided(r.data);
    } catch (e) {
      fail(errFields(e), errMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (reason) => {
    clearTimeout(timer.current);
    try {
      const r = await api.post(`/take5s/${doc.id}/stop`, { reason, data: d });
      decided.current = true;
      toast.success("Travail arrêté : la décision est enregistrée.");
      onDecided(r.data);
      return null;
    } catch (e) {
      return errMessage(e);
    }
  };

  const end = d.duration_minutes ? addMinutes(doc.start_at, d.duration_minutes) : null;
  const nErr = Object.keys(errors).length;

  return (
    <div className="space-y-5">
      <Take5Header doc={viewDoc} durationOverride={d.duration_minutes} plannedEndOverride={end} />
      {doc.parent_id && (
        <div data-testid="take5-revision-banner" className="rounded-lg border border-orange-500/60 bg-orange-950/40 p-3 text-sm text-orange-100">
          Réévaluation (révision {doc.revision}) — raison : <span className="whitespace-pre-wrap">{doc.revision_reason}</span>{" "}
          <Link to={`/take5/${doc.parent_id}`} data-testid="take5-parent-link" className="font-semibold underline">Voir la version précédente</Link>
        </div>
      )}
      <Context d={d} refs={refs} errors={errors} set={set} />
      <Questions questions={doc.questions} answers={d.answers} errors={errors} onAnswer={(qid, v) => set({ answers: { ...d.answers, [qid]: v } }, `answers.${qid}`)} />
      <Hazards families={refs.families.filter((f) => !(refs.site_hidden?.[d.site_id] || []).includes(f.id))} measures={refs.measures} links={refs.hazard_links || {}} seed={doc.root_id || doc.id} hazards={d.hazards} noHazard={d.no_hazard_confirmed} errors={errors}
        onHazards={(hazards) => set({ hazards }, "hazards")} onNoHazard={(v) => set({ no_hazard_confirmed: v }, "no_hazard_confirmed")} />
      <Photos doc={doc} d={d} set={set} />

      <div data-testid="take5-decision-bar" className="sticky bottom-[72px] z-20 rounded-xl border border-border bg-card/95 p-3 backdrop-blur-xl sm:p-4 md:bottom-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span data-testid="autosave-status" className="flex items-center gap-1 text-muted-foreground">
            {saveState.status === "saving" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Enregistrement automatique…</>}
            {saveState.status === "saved" && <><CloudCheck className="h-3.5 w-3.5 text-emerald-300" /> Brouillon enregistré automatiquement à {fmtTime(saveState.at, siteObj)}</>}
            {saveState.status === "error" && <span className="flex items-center gap-1 text-red-300"><CloudAlert className="h-3.5 w-3.5" /> {saveState.msg}</span>}
            {saveState.status === "idle" && "Vos saisies sont enregistrées automatiquement."}
          </span>
          {nErr > 0 && <span data-testid="take5-error-count" role="alert" className="font-semibold text-red-300">{nErr} point(s) à corriger</span>}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <Button data-testid="authorize-task-button" onClick={authorize} disabled={submitting} className="h-14 px-2 text-sm font-bold sm:text-base">
            {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ShieldCheck className="mr-2 h-5 w-5" />} Autoriser la tâche
          </Button>
          <StopButton onStop={stop} disabled={submitting} />
        </div>
      </div>
    </div>
  );
};
